import { Accordion, AccordionDetails, Grid, Typography, useEventCallback } from '@mui/material';
import { memo, useState } from 'react';
import { ExpandMore } from '@mui/icons-material';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import PmiTextField from 'shell/components/shared/text-field';
import { AnprForm } from 'system/view-models/operator';
import { DatePicker } from '@mui/lab';
import { FormikErrors, FormikTouched } from 'formik';
import { DateTime } from 'luxon';
import { noop } from 'shell/utils/noop';
import StationIdAnprList, {
  StationIdAnprListVM,
} from 'shell/components/shared/station-id-anpr-list';
import { useListener } from 'shell/hooks/use-bus';
import { ANPR_ACCORDION_EVENT } from './constants';

interface AnprProps {
  title: string;
  anprForm?: AnprForm;
  disabled?: boolean;
  isDetail?: boolean;
  onChanges: (values: AnprForm) => void;
  errors?: FormikErrors<AnprForm>;
  touched?: FormikTouched<AnprForm>;
  fiscalCode?: string;
}

const Anpr = ({
  anprForm,
  title,
  isDetail,
  disabled = false,
  onChanges,
  touched,
  errors,
  fiscalCode,
}: AnprProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(false);

  const onChangeValidityStart = useEventCallback((date: DateTime | null) => {
    if (date || anprForm?.validityEnd || anprForm?.stationIdAnpr) {
      onChanges({
        validityStart: date || null,
        isEditAnprData: true,
        anprOperatorCode: fiscalCode || undefined,
      });
    } else {
      onChanges({
        validityStart: date || null,
        isEditAnprData: false,
        anprOperatorCode: undefined,
      });
    }
  });

  const onChangeValidityEnd = useEventCallback((date: DateTime | null) => {
    if (date || anprForm?.validityStart || anprForm?.stationIdAnpr) {
      onChanges({
        validityEnd: date || null,
        isEditAnprData: true,
        anprOperatorCode: fiscalCode || undefined,
      });
    } else {
      onChanges({
        validityEnd: date || null,
        isEditAnprData: false,
        anprOperatorCode: undefined,
      });
    }
  });

  const onChangeStationIdAnpr = useEventCallback((value: StationIdAnprListVM | null) => {
    if (value?.description || anprForm?.validityStart || anprForm?.validityEnd) {
      onChanges({
        stationIdAnpr: value || null,
        isEditAnprData: true,
        anprOperatorCode: fiscalCode || undefined,
      });
    } else {
      onChanges({
        stationIdAnpr: value || null,
        isEditAnprData: false,
        anprOperatorCode: undefined,
      });
    }
  });

  useListener<boolean>(ANPR_ACCORDION_EVENT, (anprOpen) => {
    setOpen(!anprOpen);
  });

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-operators-content"
        id="panel-operators-header"
        classes={{
          root: 'pmi-flex-row-reverse',
        }}
        expandIcon={<ExpandMore />}
      >
        <Typography>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div className="pmi-mt-2">
          <Grid item container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <PmiTextField
                label={`Cd Operatore ANPR ${
                  anprForm?.isRequiredAnpr || anprForm?.isEditAnprData ? '*' : ''
                }`}
                aria-label="codice operatore anpr"
                id="anprOperatorCode"
                name="anprOperatorCode"
                fullWidth
                inputProps={{
                  maxLength: '16',
                }}
                type="text"
                variant="outlined"
                value={anprForm?.anprOperatorCode || ''}
                disabled
                onChange={noop}
                error={Boolean(errors?.anprOperatorCode)}
                helperText={errors?.anprOperatorCode}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label={`Data inizio validità ${
                  anprForm?.isRequiredAnpr || anprForm?.isEditAnprData ? '*' : ''
                }`}
                aria-label="Data inizio validità"
                value={anprForm?.validityStart || null}
                disabled={!anprForm?.isRequiredAnpr || isDetail}
                onChange={(value) => onChangeValidityStart(value)}
                renderInput={(params) => (
                  <PmiTextField
                    id="validityStart"
                    name="validityStart"
                    {...params}
                    fullWidth
                    error={touched?.validityStart && Boolean(errors?.validityStart)}
                    helperText={errors?.validityStart}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Data fine validità"
                aria-label="Data fine validità"
                value={anprForm?.validityEnd || null}
                disabled={!anprForm?.isRequiredAnpr || isDetail}
                onChange={(value) => onChangeValidityEnd(value)}
                renderInput={(params) => (
                  <PmiTextField
                    id="validityEnd"
                    name="validityEnd"
                    {...params}
                    fullWidth
                    error={false}
                    helperText=""
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StationIdAnprList
                disabled={disabled}
                value={anprForm?.stationIdAnpr || null}
                onChange={onChangeStationIdAnpr}
                error={touched?.stationIdAnpr && Boolean(errors?.stationIdAnpr)}
                required={anprForm?.isRequiredAnpr || anprForm?.isEditAnprData}
                label="Cd Postazione ANPR"
                id="stationIdAnpr"
              />
            </Grid>
          </Grid>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

Anpr.displayName = 'Anpr accordion component';

const MemoizedAnpr = memo(Anpr);

export default MemoizedAnpr as typeof Anpr;
